import { longDate, usd } from '../agreementFormat';
import { PageTitle, Paper } from './Paper';
import { Declined, ScopeHeading } from './ScopePage';
import { withHeads } from './scopeHeads';

const Row = ({ label, cents, strong, tid, tag }) => (
  <div className={`flex items-baseline justify-between gap-4 py-2 ${strong ? '' : 'border-b border-[#E6E8EC]'}`} data-testid={tid}>
    <span className={`min-w-0 ${strong ? 'font-exo text-sm font-bold' : 'text-[13px]'} text-[#111417]`}>
      {label}{tag && <span className="ml-2 rounded-[2px] border border-[#D5D8DD] px-1 py-px text-[8px] font-semibold uppercase tracking-[0.14em] text-[#5C6370]">{tag}</span>}
    </span>
    {cents != null && <span className={`shrink-0 tabular-nums ${strong ? 'font-exo text-lg font-bold' : 'font-exo text-[13px] font-semibold'}`} data-testid={`${tid}-amount`}>{usd(cents)}</span>}
  </div>
);

const taxLabel = (t) => (t.exempt ? `${t.label} (tax exempt)` : `${t.label} (${Number(t.pct).toLocaleString('en-US', { maximumFractionDigits: 3 })}%)`);

// Sales to cash 1: the payment terms under the total (each payment, when it's due, its share and amount). 2026-11: the
// services on their own schedule follow the main one ("All other services"), each under its name and total.
export const termGroups = (doc) => {
  const own = doc.service_terms || [];
  return [{ key: 'main', name: own.length ? 'All other services' : null, cents: doc.terms_rest_cents, terms: doc.terms || [] },
    ...own.map((s) => ({ key: s.key, name: s.name || 'Service', cents: s.total_cents, terms: s.terms }))].filter((g) => g.terms.length);
};

export const hasTerms = (doc) => termGroups(doc).length > 0;

export function TermsRows({ doc, b }) {
  const groups = termGroups(doc);
  if (!groups.length) return null;
  let n = 0;
  return (
    <div className="mt-6" data-testid="agreement-preview-terms">
      <p className="mb-1 text-[9px] font-semibold uppercase tracking-[0.2em]" style={{ color: b.accent }}>Payment terms</p>
      {groups.map((g, gi) => (
        <div key={g.key} data-testid={`agreement-preview-terms-group-${gi}`}>
          {g.name && (
            <p className="mt-3 flex items-baseline justify-between gap-4 border-b pb-1 font-exo text-[12px] font-bold text-[#111417]" style={{ borderColor: b.primary }} data-testid={`agreement-preview-terms-group-${gi}-name`}>
              <span>{g.name}</span><span className="tabular-nums">{usd(g.cents)}</span></p>
          )}
          {g.terms.map((t) => {
            const i = n++;
            return (
              <div key={i} className="flex items-baseline justify-between gap-4 border-b border-[#E6E8EC] py-2" data-testid={`agreement-preview-term-${i}`}>
                <span className="min-w-0 text-[13px] text-[#111417]">{t.name}
                  <span className="block text-[11px] text-[#5C6370]">{[t.share, t.trigger].filter(Boolean).join(' · ')}</span></span>
                <span className="shrink-0 font-exo text-[13px] font-semibold tabular-nums" data-testid={`agreement-preview-term-${i}-amount`}>{usd(t.amount_cents)}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Sales tax: Subtotal + the tax line above the total, or (built into prices) a note under it; exempt shows its $0 line.
export function TaxRows({ totals, where }) {
  const t = totals.tax;
  if (!t) return null;
  if (t.included) {
    return t.exempt ? <Row label={taxLabel(t)} cents={0} tid={`${where}-tax`} />
      : <p className="pt-1 text-[11px] text-[#5C6370]" data-testid={`${where}-tax-included`}>Prices include {t.label}.</p>;
  }
  return (
    <>
      <Row label="Subtotal" cents={totals.subtotal_cents} tid={`${where}-subtotal`} />
      <Row label={taxLabel(t)} cents={t.cents} tid={`${where}-tax`} />
    </>
  );
}

// The price, at the chosen detail level. Every level adds up to the same total. Optional services (no client picks)
// sit apart, outside the total; declined ones (client picks): names only, after it.
export default function InvestmentPage({ pg, doc, n, total }) {
  const b = doc.brand;
  const base = doc.services.filter((s) => !s.optional);
  const extra = doc.services.filter((s) => s.optional);
  const byService = doc.service_titles !== false;
  return (
    <Paper brand={b} n={n} total={total} tid="agreement-preview-investment">
      <PageTitle brand={b} eyebrow="Investment" title={pg.title || 'Your investment'} />
      <div>
        {byService && doc.detail !== 'lump' && withHeads(doc, base).map(({ head, item: s }, i) => (
          <div key={s.key}>
            {head != null && <ScopeHeading name={head} b={b} tid={`agreement-preview-inv-scopehead-${i}`} />}
            <Row label={s.name} cents={s.total_cents} tid={`agreement-preview-inv-${i}`} />
          </div>
        ))}
        {byService && doc.detail === 'lump' && <p className="pb-2 text-[13px] text-[#111417]" data-testid="agreement-preview-inv-lump">{base.map((s) => s.name).join(' · ')}</p>}
        {!doc.totals.tax?.included && <TaxRows totals={doc.totals} where="agreement-preview" />}
        <div className="mt-2 border-t-2 pt-1" style={{ borderColor: b.primary }}>
          <Row label="Total investment" cents={doc.totals.base_cents} strong tid="agreement-preview-total" />
        </div>
        {doc.totals.tax?.included && <TaxRows totals={doc.totals} where="agreement-preview" />}
      </div>
      <TermsRows doc={doc} b={b} />
      {extra.length > 0 && (
        <div className="mt-6" data-testid="agreement-preview-optional">
          <p className="mb-1 text-[9px] font-semibold uppercase tracking-[0.2em]" style={{ color: b.accent }}>Optional additions</p>
          {extra.map((s, i) => <Row key={s.key} label={s.name} cents={s.total_cents} tag="Optional" tid={`agreement-preview-opt-${i}`} />)}
          {doc.totals.optional_tax_cents > 0 && <Row label={`${doc.totals.tax.label} on optional additions`} cents={doc.totals.optional_tax_cents} tid="agreement-preview-opt-tax" />}
        </div>
      )}
      <Declined names={doc.declined} b={b} tid="agreement-preview-inv-declined" />
      {doc.valid_through && <p className="mt-6 text-[11px] text-[#5C6370]">This pricing holds through {longDate(doc.valid_through)}.</p>}
    </Paper>
  );
}
